import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Loader2 } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../ui/form';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';

type SupplierCommercialMeta = {
  desvio_comercial_pct?: string;
  beneficio_comercial_pct?: string;
};

export type AutocompleteOption = {
  label: string;
  value: string;
  meta?: SupplierCommercialMeta;
};

export type SubprojectCreateCatalogItemForm = {
  roomName: string;
  quantity: number;
  ref: string;
  supplierName: string;
  supplierId: string;
  description: string;
  categoryName: string;
  categoryId: string;
  unitCost: string;
  desvioComercialPct: string;
  beneficioComercialPct: string;
  montajePct: string;
  customDesvioComercial: boolean;
  customBeneficioComercial: boolean;
};

export interface SubprojectCreateCatalogItemDialogProps {
  open: boolean;
  initialValues?: Partial<SubprojectCreateCatalogItemForm>;
  isSubmitting?: boolean;
  onSubmit: (values: SubprojectCreateCatalogItemForm) => Promise<void> | void;
  onClose: () => void;
  roomOptions?: AutocompleteOption[];
  supplierOptions?: AutocompleteOption[];
  supplierLoading?: boolean;
  onSupplierSearch?: (query: string) => void;
  categoryOptions?: AutocompleteOption[];
  categoryLoading?: boolean;
  onCategorySearch?: (query: string) => void;
}

const EMPTY_VALUES: SubprojectCreateCatalogItemForm = {
  roomName: '',
  quantity: 1,
  ref: '',
  supplierName: '',
  supplierId: '',
  description: '',
  categoryName: '',
  categoryId: '',
  unitCost: '',
  desvioComercialPct: '0',
  beneficioComercialPct: '0',
  montajePct: '0',
  customDesvioComercial: false,
  customBeneficioComercial: false,
};

const parseDecimalInput = (value: string) => {
  const parsed = Number.parseFloat(String(value ?? '').replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeDecimalText = (value: string) => value.replace(',', '.');
const formatDecimal = (value: number) => value.toFixed(2);

const computePvp = (unitCost: number, beneficioComercialPct: number, montajePct: number) =>
  unitCost * (1 + beneficioComercialPct / 100) * (1 + montajePct / 100);

const computeCost = (unitCost: number, desvioComercialPct: number) =>
  unitCost * (1 - desvioComercialPct / 100);

const toPctString = (value: unknown) => {
  const parsed = Number(value ?? 0);
  if (!Number.isFinite(parsed)) return '0.00';
  return parsed.toFixed(2);
};

type LookupAutocompleteProps = {
  value: string;
  onTextChange: (next: string) => void;
  onSelect: (next: AutocompleteOption) => void;
  options: AutocompleteOption[];
  loading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  onSearch?: (query: string) => void;
};

function LookupAutocomplete({
  value,
  onTextChange,
  onSelect,
  options,
  loading,
  disabled,
  placeholder,
  onSearch,
}: LookupAutocompleteProps) {
  const [openDropdown, setOpenDropdown] = useState(false);
  const [search, setSearch] = useState(value || '');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const skipOpenRef = useRef(false);

  useEffect(() => {
    setSearch(value || '');
  }, [value]);

  const filtered = useMemo(
    () =>
      options.filter((option) => option.label.toLowerCase().includes(search.trim().toLowerCase())),
    [options, search],
  );

  const handleSelect = (option: AutocompleteOption) => {
    skipOpenRef.current = true;
    onSelect(option);
    setSearch(option.label);
    setOpenDropdown(false);
    inputRef.current?.blur();
  };

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        value={search}
        placeholder={placeholder}
        onFocus={() => {
          if (skipOpenRef.current) {
            skipOpenRef.current = false;
            return;
          }
          onSearch?.(search);
          setOpenDropdown(true);
        }}
        onChange={(event) => {
          const next = event.target.value;
          setSearch(next);
          onTextChange(next);
          onSearch?.(next);
          setOpenDropdown(true);
        }}
        onBlur={() => {
          setTimeout(() => setOpenDropdown(false), 120);
        }}
        autoComplete="off"
        disabled={disabled}
      />
      {openDropdown ? (
        <div className="absolute z-50 mt-1 max-h-64 w-full overflow-auto rounded-md border bg-white shadow-lg">
          {loading ? <div className="px-3 py-2 text-sm text-gray-500">Cargando…</div> : null}
          {!loading && filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">Sin resultados</div>
          ) : (
            filtered.map((option) => (
              <button
                type="button"
                key={option.value}
                className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => handleSelect(option)}
              >
                {option.label}
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}

export function SubprojectCreateCatalogItemDialog({
  open,
  initialValues,
  isSubmitting = false,
  onSubmit,
  onClose,
  roomOptions = [],
  supplierOptions = [],
  supplierLoading,
  onSupplierSearch,
  categoryOptions = [],
  categoryLoading,
  onCategorySearch,
}: SubprojectCreateCatalogItemDialogProps) {
  const computedInitialValues = useMemo(
    () => ({
      ...EMPTY_VALUES,
      ...initialValues,
      quantity: initialValues?.quantity ?? 1,
      unitCost: initialValues?.unitCost != null ? String(initialValues.unitCost) : '',
      montajePct: initialValues?.montajePct != null ? String(initialValues.montajePct) : '0',
      desvioComercialPct:
        initialValues?.desvioComercialPct != null ? String(initialValues.desvioComercialPct) : '0',
      beneficioComercialPct:
        initialValues?.beneficioComercialPct != null
          ? String(initialValues.beneficioComercialPct)
          : '0',
      supplierId: initialValues?.supplierId ?? '',
      categoryId: initialValues?.categoryId ?? '',
      customDesvioComercial: initialValues?.customDesvioComercial ?? false,
      customBeneficioComercial: initialValues?.customBeneficioComercial ?? false,
    }),
    [initialValues],
  );

  const form = useForm<SubprojectCreateCatalogItemForm>({ defaultValues: computedInitialValues });

  const supplierId = form.watch('supplierId');
  const supplierName = form.watch('supplierName');
  const unitCost = parseDecimalInput(form.watch('unitCost'));
  const desvioComercialPct = parseDecimalInput(form.watch('desvioComercialPct'));
  const beneficioComercialPct = parseDecimalInput(form.watch('beneficioComercialPct'));
  const montajePct = parseDecimalInput(form.watch('montajePct'));
  const customDesvioComercial = form.watch('customDesvioComercial');
  const customBeneficioComercial = form.watch('customBeneficioComercial');

  const selectedSupplierMeta = useMemo(() => {
    if (supplierId) {
      const byId = supplierOptions.find((option) => option.value === supplierId);
      if (byId?.meta) return byId.meta;
    }

    const normalizedName = supplierName.trim().toLowerCase();
    if (!normalizedName) return undefined;
    return supplierOptions.find((option) => option.label.trim().toLowerCase() === normalizedName)?.meta;
  }, [supplierId, supplierName, supplierOptions]);

  useEffect(() => {
    if (!open) return;
    form.reset(computedInitialValues);
    onSupplierSearch?.(computedInitialValues.supplierName);
    onCategorySearch?.(computedInitialValues.categoryName);
  }, [open, computedInitialValues, form, onSupplierSearch, onCategorySearch]);

  useEffect(() => {
    if (!selectedSupplierMeta) return;

    if (!customDesvioComercial) {
      form.setValue('desvioComercialPct', toPctString(selectedSupplierMeta.desvio_comercial_pct), {
        shouldDirty: true,
      });
    }

    if (!customBeneficioComercial) {
      form.setValue(
        'beneficioComercialPct',
        toPctString(selectedSupplierMeta.beneficio_comercial_pct),
        { shouldDirty: true },
      );
    }
  }, [
    customBeneficioComercial,
    customDesvioComercial,
    form,
    selectedSupplierMeta,
    supplierId,
    supplierName,
  ]);

  const handleSubmit = form.handleSubmit(async (values) => {
    await onSubmit({
      ...values,
      quantity: Number(values.quantity) || 0,
    });
  });

  const pvpCalculado = formatDecimal(computePvp(unitCost, beneficioComercialPct, montajePct));
  const costoCalculado = formatDecimal(computeCost(unitCost, desvioComercialPct));

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && !isSubmitting) onClose(); }}>
      <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-[40rem]">
        <DialogHeader>
          <DialogTitle>Crear item (catálogo + subproyecto)</DialogTitle>
          <DialogDescription>
            Crea el item en catálogo con la nueva lógica de precios y añade su foto actual al subproyecto.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="grid gap-4 sm:grid-cols-3">
              <FormField
                control={form.control}
                name="roomName"
                rules={{ required: 'Estancia obligatoria' }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Estancia</FormLabel>
                    <FormControl>
                      <Input list="room-suggestions" disabled={isSubmitting} {...field} />
                    </FormControl>
                    <datalist id="room-suggestions">
                      {roomOptions.map((option) => (
                        <option key={option.value} value={option.label} />
                      ))}
                    </datalist>
                    <FormMessage />
                  </FormItem>
                )}
              />
                            <FormField
                control={form.control}
                name="categoryName"
                rules={{
                  required: 'Categoría obligatoria',
                  validate: () => (form.getValues('categoryId') ? true : 'Selecciona una categoría válida.'),
                }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Categoría</FormLabel>
                    <FormControl>
                      <LookupAutocomplete
                        value={field.value}
                        disabled={isSubmitting}
                        options={categoryOptions}
                        loading={categoryLoading}
                        placeholder="Busca y selecciona una categoría"
                        onSearch={onCategorySearch}
                        onTextChange={(next) => {
                          field.onChange(next);
                          form.setValue('categoryId', '');
                        }}
                        onSelect={({ label, value }) => {
                          field.onChange(label);
                          form.setValue('categoryId', value, { shouldValidate: true });
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="quantity"
                rules={{ min: { value: 1, message: 'Mínimo 1 unidad' } }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cantidad</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={1}
                        step={1}
                        inputMode="numeric"
                        disabled={isSubmitting}
                        value={field.value}
                        onChange={(event) => field.onChange(Number(event.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="ref"
                rules={{ required: 'Referencia obligatoria' }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Referencia</FormLabel>
                    <FormControl>
                      <Input disabled={isSubmitting} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="supplierName"
                rules={{
                  required: 'Proveedor obligatorio',
                  validate: () => (form.getValues('supplierId') ? true : 'Selecciona un proveedor válido.'),
                }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Proveedor</FormLabel>
                    <FormControl>
                      <LookupAutocomplete
                        value={field.value}
                        disabled={isSubmitting}
                        options={supplierOptions}
                        loading={supplierLoading}
                        placeholder="Busca y selecciona un proveedor"
                        onSearch={onSupplierSearch}
                        onTextChange={(next) => {
                          field.onChange(next);
                          form.setValue('supplierId', '');
                        }}
                        onSelect={({ label, value, meta }) => {
                          field.onChange(label);
                          form.setValue('supplierId', value, { shouldValidate: true });
                          if (!form.getValues('customDesvioComercial')) {
                            form.setValue('desvioComercialPct', toPctString(meta?.desvio_comercial_pct), {
                              shouldDirty: true,
                            });
                          }
                          if (!form.getValues('customBeneficioComercial')) {
                            form.setValue(
                              'beneficioComercialPct',
                              toPctString(meta?.beneficio_comercial_pct),
                              { shouldDirty: true },
                            );
                          }
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="description"
              rules={{ required: 'Describe el producto' }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descripción</FormLabel>
                  <FormControl>
                    <Textarea rows={3} disabled={isSubmitting} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid gap-4 sm:grid-cols-3">

              <FormField
                control={form.control}
                name="unitCost"
                rules={{ required: 'Precio tarifa requerido' }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Precio Tarifa (€)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        inputMode="decimal"
                        disabled={isSubmitting}
                        {...field}
                        onChange={(event) => field.onChange(normalizeDecimalText(event.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
                            <FormItem>
                <FormLabel>PVP (€)</FormLabel>
                <FormControl>
                  <Input value={pvpCalculado} readOnly disabled />
                </FormControl>
              </FormItem>
                            <FormItem>
                <FormLabel>Costo (€)</FormLabel>
                <FormControl>
                  <Input value={costoCalculado} readOnly disabled />
                </FormControl>
              </FormItem>

            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <FormField
                control={form.control}
                name="desvioComercialPct"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Desvío comercial (%)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        inputMode="decimal"
                        disabled={isSubmitting || !customDesvioComercial}
                        {...field}
                        onChange={(event) => field.onChange(normalizeDecimalText(event.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="beneficioComercialPct"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Beneficio comercial (%)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        inputMode="decimal"
                        disabled={isSubmitting || !customBeneficioComercial}
                        {...field}
                        onChange={(event) => field.onChange(normalizeDecimalText(event.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}

              />
              <FormField
                control={form.control}
                name="montajePct"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Montaje (%)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        inputMode="decimal"
                        disabled={isSubmitting}
                        {...field}
                        onChange={(event) => field.onChange(normalizeDecimalText(event.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="mt-1 flex items-center gap-2">
                <Checkbox
                  id="subprojectCustomDesvioComercial"
                  checked={customDesvioComercial}
                  disabled={isSubmitting}
                  onCheckedChange={(checked) => {
                    const enabled = checked === true;
                    form.setValue('customDesvioComercial', enabled, { shouldDirty: true });
                    if (!enabled) {
                      form.setValue(
                        'desvioComercialPct',
                        toPctString(selectedSupplierMeta?.desvio_comercial_pct),
                        { shouldDirty: true },
                      );
                    }
                  }}
                />
                <label htmlFor="subprojectCustomDesvioComercial" className="text-xs text-gray-600">
                  Personalizar
                </label>
              </div>
              <div className="mt-1 flex items-center gap-2">
                <Checkbox
                  id="subprojectCustomBeneficioComercial"
                  checked={customBeneficioComercial}
                  disabled={isSubmitting}
                  onCheckedChange={(checked) => {
                    const enabled = checked === true;
                    form.setValue('customBeneficioComercial', enabled, { shouldDirty: true });
                    if (!enabled) {
                      form.setValue(
                        'beneficioComercialPct',
                        toPctString(selectedSupplierMeta?.beneficio_comercial_pct),
                        { shouldDirty: true },
                      );
                    }
                  }}
                />
                <label htmlFor="subprojectCustomBeneficioComercial" className="text-xs text-gray-600">
                  Personalizar
                </label>
              </div>
              <div aria-hidden />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={isSubmitting}>
                {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Crear y añadir'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
